import { buildJoinInvitePath } from "./join-after-auth";
import {
  INVITED_BY_QUERY_PARAM,
  parseInvitedByUserId,
} from "./party-join-attribution";
import { isPartyBallotComplete } from "./tie-breakers";

export const SOCIAL_PARTY_SIZE_HINT = 4;
export const PARTY_INVITE_CODE_LENGTH = 8;
export const CATEGORY_PREVIEW_COUNT = 4;
export const DISABLED_LEADERBOARD_HINT = "Needs 2+ players";
export const CATEGORY_ANCHOR_PREFIX = "category-";

/** No 0/O, 1/l/I. Remaining letters stay mixed-case. */
export const PARTY_INVITE_CODE_ALPHABET =
  "23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz";

const UNICODE_DASHES = /[\u2010-\u2015]/g;

export function shouldPinInviteCode(joinedParticipantCount: number): boolean {
  return joinedParticipantCount < SOCIAL_PARTY_SIZE_HINT;
}

export function formatInviteCodeForDisplay(code: string): string {
  return code.replace(UNICODE_DASHES, "-").trim();
}

export function normalizeInviteCodeForLookup(code: string): string[] {
  const hyphenated = code
    .replace(UNICODE_DASHES, "-")
    .replace(/\s+/g, "")
    .trim();
  const compact = hyphenated.replace(/-/g, "");
  return hyphenated === compact ? [hyphenated] : [hyphenated, compact];
}

export function joinPathFromEnteredCode(raw: string): string | null {
  const pasted = inviteCodeFromPastedValue(raw);
  const [normalized] = normalizeInviteCodeForLookup(pasted.code);
  if (!normalized) {
    return null;
  }

  return buildJoinInvitePath(
    encodeURIComponent(normalized),
    null,
    pasted.invitedByUserId,
  );
}

function inviteCodeFromPastedValue(raw: string): {
  code: string;
  invitedByUserId: string | null;
} {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { code: "", invitedByUserId: null };
  }

  try {
    const url = new URL(
      trimmed.includes("://") ? trimmed : `https://${trimmed}`,
    );
    const parts = url.pathname.split("/").filter(Boolean);
    const joinIndex = parts.findIndex(
      (part) => part.toLowerCase() === "join",
    );
    const code = joinIndex >= 0 ? parts[joinIndex + 1] : undefined;
    if (code) {
      return {
        code: decodeURIComponent(code),
        invitedByUserId: parseInvitedByUserId(
          url.searchParams.get(INVITED_BY_QUERY_PARAM),
        ),
      };
    }
  } catch {
    // Typed codes are not URLs.
  }

  return { code: trimmed, invitedByUserId: null };
}

export function isUnambiguousInviteCode(code: string): boolean {
  const compact = code.replace(/-/g, "");
  if (compact.length !== PARTY_INVITE_CODE_LENGTH) {
    return false;
  }

  return [...compact].every((char) => PARTY_INVITE_CODE_ALPHABET.includes(char));
}

export function generatePartyInviteCode(
  randomByte: () => number = defaultRandomByte,
): string {
  let compact = "";
  for (let i = 0; i < PARTY_INVITE_CODE_LENGTH; i++) {
    const index = randomByte() % PARTY_INVITE_CODE_ALPHABET.length;
    compact += PARTY_INVITE_CODE_ALPHABET[index] ?? PARTY_INVITE_CODE_ALPHABET[0];
  }
  return compact;
}

export type PartyDashboardState =
  | "solo"
  | "underfilled"
  | "needs_vote"
  | "waiting_on_others"
  | "locked_preshow"
  | "live"
  | "ended";

export type PartyDashboardPrimaryAction =
  | "invite"
  | "vote"
  | "nudge"
  | "compare_picks"
  | "leaderboard"
  | "final_standings"
  | "chat";

export interface PartyDashboardModel {
  state: PartyDashboardState;
  primaryAction: PartyDashboardPrimaryAction;
}

export interface PartyDashboardModelInput {
  joinedParticipantCount: number;
  totalCategories: number;
  userVoteCount: number;
  predictionsReady: boolean;
  isLocked: boolean;
  showHasStarted: boolean;
  announcedResultsCount: number;
  othersHavePendingVotes: boolean;
  isOwner: boolean;
}

export interface PartyDashboardPartyInput {
  currentUserId: string;
  ownerId: string;
  userVoteCount: number;
  announcedResultsCount: number;
  isLocked: boolean;
  voteCountByUserId: Record<string, number>;
  participants: readonly {
    userId: string;
    user: { clerkId: string };
  }[];
  awardShow: {
    startTime?: Date | string | null;
    categories: readonly { nominees: readonly unknown[] }[];
  };
}

export function isPendingPartyParticipant(clerkId: string): boolean {
  return clerkId.startsWith("pending:");
}

export function getJoinedPartyParticipants<
  T extends { user: { clerkId: string } },
>(participants: readonly T[]): T[] {
  return participants.filter(
    (participant) => !isPendingPartyParticipant(participant.user.clerkId),
  );
}

export function getPartyShowContext(
  party: Pick<PartyDashboardPartyInput, "announcedResultsCount" | "awardShow">,
  now: Date = new Date(),
): {
  totalCategories: number;
  totalNominees: number;
  predictionsReady: boolean;
  showHasStarted: boolean;
  showIsComplete: boolean;
} {
  const totalCategories = party.awardShow.categories.length;
  const totalNominees = party.awardShow.categories.reduce(
    (sum, category) => sum + category.nominees.length,
    0,
  );
  const showStartTime = party.awardShow.startTime
    ? new Date(party.awardShow.startTime)
    : null;

  return {
    totalCategories,
    totalNominees,
    predictionsReady: totalCategories > 0 && totalNominees > 0,
    showHasStarted: Boolean(showStartTime && now >= showStartTime),
    showIsComplete:
      totalCategories > 0 && party.announcedResultsCount >= totalCategories,
  };
}

export function getPrimaryActionHref(
  action: PartyDashboardPrimaryAction,
  partyId: string,
): string {
  switch (action) {
    case "vote":
      return `/parties/${partyId}/vote`;
    case "chat":
      return `/parties/${partyId}/chat?entry=nudge`;
    case "nudge":
      return getPartyParticipantsHref(partyId, { focusNonVoters: true });
    case "compare_picks":
      return `/parties/${partyId}/votes`;
    case "leaderboard":
    case "final_standings":
      return `/parties/${partyId}/leaderboard`;
    case "invite":
    default:
      return `/parties/${partyId}/invite`;
  }
}

function defaultRandomByte(): number {
  const bytes = new Uint8Array(1);
  crypto.getRandomValues(bytes);
  return bytes[0] ?? 0;
}

export const PARTY_PARTICIPANTS_FOCUS_PARAM = "focus";
export const PARTY_PARTICIPANTS_FOCUS_NON_VOTERS = "non-voters";

export type ParticipantBallotStatus =
  | "unavailable"
  | "not_started"
  | "in_progress"
  | "complete";

export function getParticipantBallotStatus(input: {
  userVoteCount: number;
  totalCategories: number;
}): ParticipantBallotStatus {
  if (input.totalCategories <= 0) {
    return "unavailable";
  }

  if (input.userVoteCount <= 0) {
    return "not_started";
  }

  if (isPartyBallotComplete(input)) {
    return "complete";
  }

  return "in_progress";
}

export function isParticipantBallotPending(
  status: ParticipantBallotStatus,
): boolean {
  return status === "not_started" || status === "in_progress";
}

export function isPartyParticipantsNonVotersFocus(
  focus: string | string[] | null | undefined,
): boolean {
  const value = Array.isArray(focus) ? focus[0] : focus;
  return value === PARTY_PARTICIPANTS_FOCUS_NON_VOTERS;
}

export function getPartyParticipantsHref(
  partyId: string,
  options?: { focusNonVoters?: boolean },
): string {
  const path = `/parties/${partyId}/participants`;
  if (!options?.focusNonVoters) {
    return path;
  }

  return `${path}?${PARTY_PARTICIPANTS_FOCUS_PARAM}=${PARTY_PARTICIPANTS_FOCUS_NON_VOTERS}`;
}

export function sortParticipantsByBallotUrgency<T extends { userId: string }>(
  participants: readonly T[],
  voteCountByUserId: Record<string, number>,
  totalCategories: number,
): T[] {
  if (totalCategories <= 0) {
    return [...participants];
  }

  return [...participants].sort((left, right) => {
    const leftPending = isParticipantBallotPending(
      getParticipantBallotStatus({
        userVoteCount: voteCountByUserId[left.userId] ?? 0,
        totalCategories,
      }),
    );
    const rightPending = isParticipantBallotPending(
      getParticipantBallotStatus({
        userVoteCount: voteCountByUserId[right.userId] ?? 0,
        totalCategories,
      }),
    );

    if (leftPending === rightPending) {
      return 0;
    }

    return leftPending ? -1 : 1;
  });
}

export function othersHavePendingVotes(
  voteCountByUserId: Record<string, number>,
  currentUserId: string,
  joinedUserIds: readonly string[],
  totalCategories: number,
): boolean {
  if (totalCategories <= 0) {
    return false;
  }

  return joinedUserIds.some((userId) => {
    if (userId === currentUserId) {
      return false;
    }
    return !isPartyBallotComplete({
      userVoteCount: voteCountByUserId[userId] ?? 0,
      totalCategories,
    });
  });
}

export function getPartyDashboardModel(
  input: PartyDashboardModelInput,
): PartyDashboardModel {
  const showIsComplete =
    input.totalCategories > 0 &&
    input.announcedResultsCount >= input.totalCategories;

  if (showIsComplete) {
    return { state: "ended", primaryAction: "final_standings" };
  }

  if (input.showHasStarted) {
    return { state: "live", primaryAction: "leaderboard" };
  }

  if (input.isLocked) {
    return { state: "locked_preshow", primaryAction: "compare_picks" };
  }

  const needsVote =
    input.predictionsReady &&
    !isPartyBallotComplete({
      userVoteCount: input.userVoteCount,
      totalCategories: input.totalCategories,
    });

  if (needsVote) {
    return { state: "needs_vote", primaryAction: "vote" };
  }

  if (input.othersHavePendingVotes && input.isOwner) {
    return { state: "waiting_on_others", primaryAction: "nudge" };
  }

  if (input.joinedParticipantCount < 2) {
    return { state: "solo", primaryAction: "invite" };
  }

  if (input.joinedParticipantCount < SOCIAL_PARTY_SIZE_HINT) {
    return { state: "underfilled", primaryAction: "invite" };
  }

  return { state: "underfilled", primaryAction: "invite" };
}

export function getPartyDashboardModelForParty(
  party: PartyDashboardPartyInput,
  now: Date = new Date(),
): PartyDashboardModel {
  const regularParticipants = getJoinedPartyParticipants(party.participants);
  const { totalCategories, predictionsReady, showHasStarted } =
    getPartyShowContext(party, now);

  return getPartyDashboardModel({
    joinedParticipantCount: regularParticipants.length,
    totalCategories,
    userVoteCount: party.userVoteCount,
    predictionsReady,
    isLocked: party.isLocked,
    showHasStarted,
    announcedResultsCount: party.announcedResultsCount,
    othersHavePendingVotes: othersHavePendingVotes(
      party.voteCountByUserId,
      party.currentUserId,
      regularParticipants.map((participant) => participant.userId),
      totalCategories,
    ),
    isOwner: party.ownerId === party.currentUserId,
  });
}

export function getPicksLinkLabel(userVoteCount: number): "Start" | "Review" {
  return userVoteCount <= 0 ? "Start" : "Review";
}

export function getCategoryAnchorId(categoryId: string): string {
  const trimmed = categoryId.trim();
  if (!trimmed) return "";
  return `${CATEGORY_ANCHOR_PREFIX}${encodeURIComponent(trimmed)}`;
}

export function getCategoryVoteHref(partyId: string, categoryId: string): string {
  return `/parties/${encodeURIComponent(partyId)}/vote#${getCategoryAnchorId(categoryId)}`;
}

export function getCategoryIdFromHash(hash: string): string | null {
  const value = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!value.startsWith(CATEGORY_ANCHOR_PREFIX)) return null;
  const encodedId = value.slice(CATEGORY_ANCHOR_PREFIX.length);
  if (!encodedId) return null;
  try {
    const categoryId = decodeURIComponent(encodedId).trim();
    return categoryId || null;
  } catch {
    return null;
  }
}

export function getCategoryPredictionLabel(
  categoryName: string,
  hasPick: boolean,
): string {
  return hasPick
    ? `Review predictions for ${categoryName}`
    : `Enter predictions for ${categoryName}`;
}

export function getVoteCtaDetail(
  userVoteCount: number,
  totalCategories: number,
): string {
  if (totalCategories <= 0) {
    return "";
  }

  if (userVoteCount <= 0) {
    return totalCategories === 1
      ? "1 category"
      : `${totalCategories} categories`;
  }

  return `${userVoteCount} of ${totalCategories}`;
}

export function shouldShowVoteProgressBar(
  userVoteCount: number,
  totalCategories: number,
): boolean {
  if (totalCategories <= 0) {
    return false;
  }

  return !isPartyBallotComplete({
    userVoteCount,
    totalCategories,
  }) && userVoteCount > 0;
}

export function shouldShowSecondaryInvite(
  primaryAction: PartyDashboardPrimaryAction,
): boolean {
  return primaryAction !== "invite";
}

const SAME_INSTANT_MS = 60_000;

export function instantsAreNearlyEqual(
  left: Date | string,
  right: Date | string,
  toleranceMs = SAME_INSTANT_MS,
): boolean {
  return Math.abs(new Date(left).getTime() - new Date(right).getTime()) < toleranceMs;
}

export function getPartySchedulePresentation(input: {
  votingClosesAt: Date | string | null | undefined;
  showStart: Date | string | null | undefined;
  now?: Date | string;
}): {
  showHasStarted: boolean;
  lockMatchesShowStart: boolean;
  showShowStartSeparately: boolean;
} {
  const showStart = input.showStart ? new Date(input.showStart) : null;
  const now = new Date(input.now ?? Date.now());
  const showHasStarted = Boolean(
    showStart && now.getTime() >= showStart.getTime(),
  );
  const lockMatchesShowStart = Boolean(
    showStart &&
      input.votingClosesAt &&
      instantsAreNearlyEqual(input.votingClosesAt, showStart),
  );

  return {
    showHasStarted,
    lockMatchesShowStart,
    showShowStartSeparately: Boolean(
      showStart && !showHasStarted && !lockMatchesShowStart,
    ),
  };
}

export function shouldShowWaitingOnBallotsNote(input: {
  nonVoterCount: number;
  isLocked: boolean;
  predictionsReady: boolean;
}): boolean {
  return (
    input.predictionsReady && !input.isLocked && input.nonVoterCount > 0
  );
}
